import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowRight,
  Camera,
  Gamepad2,
  LoaderCircle,
  LogIn,
  Mic2,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { trackEvent } from "../analytics";
import { api } from "../api";
import { signInPath } from "../authReturn";
import { isIdentityError, RecoverableError } from "../components/RecoverableError";
import { TurnstileWidget, type TurnstileWidgetHandle } from "../components/TurnstileWidget";
import { useAppStore } from "../state";
import { getUserAvatar, getUserDisplayName, useAuthSession } from "../useAuthSession";

export function JoinPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const challengeRef = useRef<TurnstileWidgetHandle>(null);
  const [code, setCode] = useState(params.code?.toUpperCase() ?? "");
  const [submittedCode, setSubmittedCode] = useState(params.code?.toUpperCase() ?? "");
  const [name, setName] = useState("");
  const [mic, setMic] = useState(false);
  const [camera, setCamera] = useState(false);
  const [entryMode, setEntryMode] = useState<"choose" | "anonymous">("choose");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const receiveChallenge = useCallback((token: string | null) => setTurnstileToken(token), []);
  const guest = useAppStore((state) => state.guest);
  const setGuest = useAppStore((state) => state.setGuest);
  const clearGuest = useAppStore((state) => state.clearGuest);
  const setRoom = useAppStore((state) => state.setRoom);
  const visibleSpectator = searchParams.get("spectator") === "1";
  const autoJoin = searchParams.get("autojoin") === "1";
  const autoJoinedCode = useRef<string | null>(null);
  const inviteSearch = new URLSearchParams({ autojoin: "1" });
  if (visibleSpectator) inviteSearch.set("spectator", "1");
  const inviteReturnTo = `/join/${encodeURIComponent(submittedCode)}?${inviteSearch.toString()}`;
  const { session, loading: authLoading } = useAuthSession();
  useEffect(() => {
    if (session && !name) setName(getUserDisplayName(session.user));
    else if (guest && !name) setName(guest.displayName);
  }, [guest, name, session]);
  const preview = useQuery({
    queryKey: ["room-preview", submittedCode],
    queryFn: () => api.previewRoom(submittedCode),
    enabled: submittedCode.length === 6,
    retry: false,
  });
  const join = useMutation({
    mutationFn: async () => {
      let identity = guest;
      if (session) {
        identity = {
          id: session.user.id,
          token: "",
          displayName: getUserDisplayName(session.user),
          avatar: getUserAvatar(session.user),
        };
      } else if (!identity) {
        if (!turnstileToken) throw new Error("Complete the security check before joining.");
        identity = await api.createGuest(turnstileToken);
        setGuest(identity);
      }
      if (!preview.data) throw new Error("Preview the room first");
      const result = await api.joinRoom(preview.data.id, {
        displayName: name.trim() || identity.displayName,
        avatar: identity.avatar,
        role: session ? (visibleSpectator ? "spectator" : "player") : "guest",
      });
      setRoom({
        roomId: preview.data.id,
        participantId: result.participantId,
        displayName: name.trim() || identity.displayName,
        avatar: identity.avatar,
        websocketUrl: result.websocketUrl,
        reconnectToken: result.reconnectToken,
        realtimeSignalToken: result.realtimeSignalToken,
        snapshot: result.room,
        lastVisitedAt: Date.now(),
      });
      return result;
    },
    onSuccess: (result) => {
      trackEvent("room_joined", {
        game_id: result.room.currentGameId ?? result.room.settings.selectedGameId ?? "waiting",
        identity_type: session ? "registered" : "guest",
        join_source: params.code ? "invite_link" : "join_code",
        role: session && visibleSpectator ? "spectator" : session ? "player" : "guest",
      });
      navigate(`/room/${result.room.roomId}`, { state: { mic, camera } });
    },
    onError: (error) => {
      trackEvent("operation_failed", { operation: "room_join", route: "/join/:code" });
      if (isIdentityError(error)) {
        clearGuest();
        setEntryMode("anonymous");
      }
      setTurnstileToken(null);
      challengeRef.current?.reset();
    },
  });
  useEffect(() => {
    if (
      !autoJoin ||
      authLoading ||
      !session ||
      !preview.data ||
      autoJoinedCode.current === submittedCode ||
      join.isPending
    )
      return;
    autoJoinedCode.current = submittedCode;
    join.mutate();
  }, [authLoading, autoJoin, join, preview.data, session, submittedCode]);
  const needsEntryChoice =
    Boolean(preview.data) && !authLoading && !session && !guest && entryMode === "choose";
  return (
    <section className="join-page">
      <div className="join-panel">
        <span className="icon-tile">
          <Gamepad2 />
        </span>
        <span className="eyebrow">DIRECT TO THE ROOM</span>
        <h1>
          {preview.data
            ? `${visibleSpectator ? "Watch" : "Join"} ${preview.data.title}`
            : "Your people are six letters away."}
        </h1>
        <p>
          {preview.data
            ? `${preview.data.participantCount} in the room · ${preview.data.currentGameId ?? "Choosing a game"}${visibleSpectator ? " · You will be visible to everyone" : ""}`
            : "Enter the code from your invite. No maze of unrelated screens."}
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!preview.data) setSubmittedCode(code.toUpperCase());
            else join.mutate();
          }}
        >
          <label>
            Room code
            <input
              className="code-input"
              value={code}
              onChange={(event) =>
                setCode(
                  event.target.value
                    .replace(/[^a-zA-Z2-9]/g, "")
                    .slice(0, 6)
                    .toUpperCase(),
                )
              }
              placeholder="TOPZLE"
              autoCapitalize="characters"
            />
          </label>
          {preview.data && authLoading && (
            <div className="invite-auth-loading" role="status">
              <LoaderCircle className="spin" /> Checking your account…
            </div>
          )}
          {needsEntryChoice && (
            <div className="invite-entry-choice">
              <div>
                <strong>How would you like to join?</strong>
                <small>Sign in to keep this room and its results, or continue as a guest.</small>
              </div>
              <Link className="button button-primary wide" to={signInPath(inviteReturnTo)}>
                <LogIn /> Sign in and join
              </Link>
              <button
                className="button button-ghost wide"
                type="button"
                onClick={() => setEntryMode("anonymous")}
              >
                <UserRound /> Play anonymously
              </button>
            </div>
          )}
          {preview.data && !authLoading && !needsEntryChoice && (
            <>
              <label>
                What should we call you?
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value.slice(0, 40))}
                  placeholder="Your nickname"
                />
              </label>
              {!session && entryMode === "anonymous" && !guest && (
                <TurnstileWidget
                  ref={challengeRef}
                  action="guest_join"
                  onToken={receiveChallenge}
                />
              )}
              <div className="permission-row">
                <button
                  className={mic ? "permission active" : "permission"}
                  type="button"
                  onClick={() => setMic(!mic)}
                >
                  <Mic2 /> Microphone <span>{mic ? "On" : "Off"}</span>
                </button>
                <button
                  className={camera ? "permission active" : "permission"}
                  type="button"
                  onClick={() => setCamera(!camera)}
                >
                  <Camera /> Camera <span>{camera ? "On" : "Off"}</span>
                </button>
              </div>
            </>
          )}
          {(!preview.data || (!authLoading && !needsEntryChoice)) && (
            <button
              className="button button-primary wide"
              type="submit"
              disabled={
                code.length !== 6 ||
                preview.isFetching ||
                join.isPending ||
                (Boolean(preview.data) &&
                  !session &&
                  entryMode === "anonymous" &&
                  !guest &&
                  !turnstileToken)
              }
            >
              {preview.isFetching || join.isPending ? (
                <>
                  <LoaderCircle className="spin" />
                  {join.isPending ? "Entering room…" : "Finding room…"}
                </>
              ) : preview.data ? (
                <>
                  {visibleSpectator ? "Enter as spectator" : "Enter room"} <ArrowRight />
                </>
              ) : (
                <>
                  Preview room <ArrowRight />
                </>
              )}
            </button>
          )}
          {(preview.error || join.error) && (
            <RecoverableError
              error={preview.error ?? join.error}
              returnTo={inviteReturnTo}
              onContinueAsGuest={() => {
                clearGuest();
                setEntryMode("anonymous");
                setTurnstileToken(null);
                join.reset();
                challengeRef.current?.reset();
              }}
              onRetry={() => {
                if (preview.error) void preview.refetch();
                else if (preview.data) join.mutate();
              }}
            />
          )}
        </form>
        <small className="privacy-note">
          <ShieldCheck /> Everyone can play. If an administrator limits guest or unverified calls,
          the room will offer sign-in or phone verification without blocking the game.
        </small>
      </div>
      <div className="join-aside">
        <div className="invite-card">
          <span>FRIDAY GAME NIGHT</span>
          <strong>
            One invite.
            <br />
            The whole night.
          </strong>
          <div className="invite-avatars">
            <i>N</i>
            <i>M</i>
            <i>J</i>
            <i>+3</i>
          </div>
          <small>Voice stays connected while games change.</small>
        </div>
      </div>
    </section>
  );
}
